/**
 * Phase-4 "Ship" settings tab — single text input for the default ship name.
 *
 * As of Phase 5, also hosts the destructive **Reset All Data** action that
 * wipes every voyage + every library entry + clears `default_ship_name`,
 * and persists a `user_initiated_reset` marker so the backend autoseeder
 * stops re-creating demo data on subsequent restarts. Guarded behind a
 * confirm dialog that requires the user to literally type `RESET`.
 *
 * Legacy lib_ships endpoints are deliberately not touched here — the user
 * still needs them around for backward-compat with existing voyages that
 * reference a ship_id, but the multi-row CRUD is no longer surfaced.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Save, Ship as ShipIcon, AlertTriangle, Trash2,
  KeyRound, Copy, Check, RefreshCw, Eye, EyeOff } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getSettings, updateSettings, resetAllData } from "@/lib/settingsApi";
import {
  getStoredToken,
  buildInviteUrl,
  maskToken,
} from "@/lib/accessApi";
import client from "@/apiClient";
import ShipLinesPanel from "@/components/ShipLinesPanel";

const REQUIRED_CONFIRM_WORD = "RESET";

export default function ShipSettingsPanel() {
  const [defaultShipName, setDefaultShipName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset-all-data state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [lastResetSummary, setLastResetSummary] = useState(null);

  const REQUIRED_ROTATE_WORD = "ROTATE";

  // Access / invite-link state.
  const [accessToken, setAccessToken] = useState(getStoredToken());
  const [revealAccess, setRevealAccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateConfirmText, setRotateConfirmText] = useState("");
  const [rotating, setRotating] = useState(false);

  const inviteUrl = accessToken ? buildInviteUrl(accessToken) : "";
  const maskedInviteDisplay = accessToken
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?token=${maskToken(accessToken)}`
    : "";

  const handleCopyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast({
        title: "Invite link copied",
        description: "Share it with someone you trust — anyone with the link gets full access.",
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Manually copy the link above instead.",
        variant: "destructive",
      });
    }
  };

  const canConfirmRotate =
    rotateConfirmText.trim().toUpperCase() === REQUIRED_ROTATE_WORD && !rotating;

  const handleRotateConfirmed = async () => {
    setRotating(true);
    try {
      const res = await client.post("/access/rotate", null, {
        params: { confirm: "YES_ROTATE" },
      });
      const newToken = res?.data?.new_token;
      const newUrl = res?.data?.invite_url || buildInviteUrl(newToken);
      if (newToken) {
        // Persist locally so the current session keeps working.
        window.localStorage.setItem("access_token", newToken);
        setAccessToken(newToken);
      }
      setRotateOpen(false);
      setRotateConfirmText("");
      toast({
        title: "Access token rotated",
        description:
          "Old invite links are now invalid. Share the new link below — yours still works.",
      });
      // Auto-copy the brand-new URL to clipboard so the operator can
      // re-share immediately.
      try {
        await navigator.clipboard.writeText(newUrl);
      } catch (clipErr) {
        // Clipboard access denied (insecure context, permission denied,
        // headless test runners). The new URL is still rendered in the
        // panel below — operator can copy manually. Log only.
        console.warn("ShipSettingsPanel: clipboard.writeText failed after token rotate", clipErr);
      }
    } catch (e) {
      toast({
        title: "Rotate failed",
        description: e?.response?.data?.detail || e.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRotating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getSettings();
      setDefaultShipName(s.default_ship_name || "");
      setOriginalName(s.default_ship_name || "");
      setUpdatedAt(s.updated_at || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = defaultShipName.trim() !== originalName.trim();
  const canSave = dirty && defaultShipName.trim().length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const s = await updateSettings({ default_ship_name: defaultShipName.trim() });
      setDefaultShipName(s.default_ship_name);
      setOriginalName(s.default_ship_name);
      setUpdatedAt(s.updated_at);
      toast({ title: "Ship name saved", description: `Default ship is now "${s.default_ship_name}".` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({
        title: "Save failed",
        description: typeof detail === "string" ? detail : JSON.stringify(detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const canConfirmReset =
    resetConfirmText.trim().toUpperCase() === REQUIRED_CONFIRM_WORD && !resetting;

  const handleResetConfirmed = async () => {
    setResetting(true);
    try {
      const res = await resetAllData();
      // Sum every deleted_count for the summary toast/text.
      const deleted = res?.deleted || {};
      const total = Object.values(deleted).reduce(
        (acc, n) => acc + (typeof n === "number" ? n : 0),
        0,
      );
      setLastResetSummary({
        at: res?.reset_at || new Date().toISOString(),
        deleted,
        total,
      });
      // The default ship name is blanked server-side too — reflect that here
      // so the user sees the field clear without a reload.
      setDefaultShipName("");
      setOriginalName("");
      setUpdatedAt("");
      setResetOpen(false);
      setResetConfirmText("");
      toast({
        title: "All data wiped",
        description: `Removed ${total.toLocaleString()} document(s) across voyages + libraries.`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({
        title: "Reset failed",
        description: typeof detail === "string" ? detail : JSON.stringify(detail),
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = () => {
    setResetConfirmText("");
    setResetOpen(true);
  };

  return (
    <div className="px-6 py-8" data-testid="settings-ship-panel">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <ShipIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight" data-testid="settings-ship-title">
            Ship
          </h1>
          <p className="text-sm text-muted-foreground">
            The default ship name that pre-fills the New Voyage wizard. Override
            per-voyage if needed.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="settings-ship-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="settings-ship-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="max-w-md space-y-4 rounded-lg border bg-card p-6 shadow-sm">
          <div className="space-y-1.5">
            <Label htmlFor="default-ship-name">Default Ship Name *</Label>
            <Input
              id="default-ship-name"
              value={defaultShipName}
              onChange={(e) => setDefaultShipName(e.target.value)}
              placeholder="e.g. Celebrity Edge"
              maxLength={200}
              data-testid="default-ship-name-input"
            />
            {updatedAt && (
              <p className="text-xs text-muted-foreground" data-testid="settings-ship-updated">
                Last updated {new Date(updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={!canSave || saving}
              data-testid="settings-ship-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
            {dirty && !saving && (
              <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
            )}
          </div>
        </div>
      )}

      {/* Phase 14 — Buffet Lines / Islands sub-section. Lives on the
          default ship's lib_ships doc; auto-creates the row on first save. */}
      <ShipLinesPanel defaultShipName={defaultShipName} />

      {/* ─────────────────────── Access / invite link ─────────────────────── */}
      <div
        className="mt-10 max-w-md rounded-lg border border-amber-300 bg-amber-50/40 p-6 shadow-sm"
        data-testid="access-section"
      >
        <div className="mb-3 flex items-center gap-2 text-amber-900">
          <KeyRound className="h-4 w-4" />
          <h2 className="text-sm font-bold tracking-wide uppercase">
            Invite Link
          </h2>
        </div>
        <p className="mb-3 text-xs text-amber-900/80 leading-relaxed">
          This workspace is private. Anyone with the invite link below has
          full access — share it with care.
        </p>
        {accessToken ? (
          <>
            <div className="mb-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                Current invite URL
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={revealAccess ? inviteUrl : maskedInviteDisplay}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="invite-link-display"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRevealAccess((v) => !v)}
                  aria-label={revealAccess ? "Hide token" : "Reveal token"}
                  data-testid="reveal-invite-btn"
                >
                  {revealAccess ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCopyInvite}
                  data-testid="copy-invite-btn"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRotateConfirmText("");
                setRotateOpen(true);
              }}
              className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="rotate-token-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Rotate token (revoke existing links)
            </Button>
          </>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            No access token in this session — sign in via your invite link first.
          </p>
        )}
      </div>

      {/* Rotate confirm dialog. Requires user to type ROTATE. */}
      <AlertDialog
        open={rotateOpen}
        onOpenChange={(o) => {
          if (rotating) return;
          setRotateOpen(o);
          if (!o) setRotateConfirmText("");
        }}
      >
        <AlertDialogContent data-testid="rotate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-900">
              <RefreshCw className="h-5 w-5" /> Rotate access token?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will{" "}
              <span className="font-semibold text-amber-900">
                invalidate the current invite link
              </span>
              . Everyone using it will be locked out and you'll need to
              re-share. Your own session will automatically pick up the new
              token (and the new URL will be copied to your clipboard).
              <br /><br />
              To confirm, type{" "}
              <span className="font-mono font-bold text-foreground">ROTATE</span>{" "}
              in the box below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-rotate-input" className="sr-only">
              Type ROTATE to confirm
            </Label>
            <Input
              id="confirm-rotate-input"
              value={rotateConfirmText}
              onChange={(e) => setRotateConfirmText(e.target.value)}
              placeholder="Type ROTATE to confirm"
              autoFocus
              disabled={rotating}
              autoComplete="off"
              data-testid="confirm-rotate-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotating} data-testid="confirm-rotate-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (canConfirmRotate) handleRotateConfirmed();
              }}
              disabled={!canConfirmRotate}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="confirm-rotate-btn"
            >
              {rotating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {rotating ? "Rotating…" : "Yes, rotate token"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─────────────────────────── Danger zone ─────────────────────────── */}
      <div
        className="mt-10 max-w-md rounded-lg border border-destructive/40 bg-destructive/5 p-6 shadow-sm"
        data-testid="danger-zone"
      >
        <div className="mb-3 flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <h2 className="text-sm font-bold tracking-wide uppercase">
            Danger zone
          </h2>
        </div>
        <p className="mb-1 text-sm font-semibold text-foreground">
          Reset all data
        </p>
        <p className="mb-4 text-xs text-muted-foreground leading-relaxed">
          Permanently delete every voyage and every library entry (ships,
          restaurants, theme buffets, live stations, crew mess themes,
          chef&apos;s specials, special events, daily events, targets).
          Clears the default ship name and stops the backend from
          auto-re-seeding demo data on the next restart. This action
          cannot be undone.
        </p>
        <Button
          variant="destructive"
          size="sm"
          onClick={openResetDialog}
          data-testid="reset-all-data-btn"
        >
          <Trash2 className="h-4 w-4" />
          Reset all data
        </Button>

        {lastResetSummary && (
          <div
            className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900"
            data-testid="reset-summary"
          >
            <p className="font-semibold">
              Reset complete · {lastResetSummary.total.toLocaleString()} document(s) removed
              <span className="ml-1 font-normal opacity-70">
                ({new Date(lastResetSummary.at).toLocaleString()})
              </span>
            </p>
            <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono leading-tight">
              {Object.entries(lastResetSummary.deleted).map(([k, n]) => (
                <li key={k}>
                  <span className="opacity-60">{k}:</span> {n}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Confirm dialog — must type RESET to enable the destructive button. */}
      <AlertDialog open={resetOpen} onOpenChange={(o) => {
        // Prevent closing the dialog mid-reset to avoid orphaning the spinner.
        if (resetting) return;
        setResetOpen(o);
        if (!o) setResetConfirmText("");
      }}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Reset all data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will{" "}
              <span className="font-semibold text-destructive">
                permanently delete every voyage and every library entry
              </span>{" "}
              from the database. The backend will no longer auto-seed
              demo data on the next restart. There is no undo.
              <br /><br />
              To confirm, type{" "}
              <span className="font-mono font-bold text-foreground">RESET</span>{" "}
              in the box below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reset-input" className="sr-only">
              Type RESET to confirm
            </Label>
            <Input
              id="confirm-reset-input"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="Type RESET to confirm"
              autoFocus
              disabled={resetting}
              autoComplete="off"
              data-testid="confirm-reset-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={resetting}
              data-testid="confirm-reset-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // AlertDialogAction auto-closes by default — prevent that
                // so we can keep the dialog open while resetting.
                e.preventDefault();
                if (canConfirmReset) handleResetConfirmed();
              }}
              disabled={!canConfirmReset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {resetting ? "Resetting…" : "Yes, wipe everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
